import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import Backend from '../backend/Backend';
import { useOktaAuth } from '@okta/okta-react';
import './UploadCsv.scss';
import { Button, Divider, FormControlLabel, Grid, Stack, Typography } from '@mui/material';
import LookupConfigMergeIcon from '../resources/images/import_merge_lconfig.svg';
import LookupConfigDeleteIcon from '../resources/images/import_delete_lconfig.svg';
import { ReactComponent as ArrowDownIcon } from '../resources/images/arrow_down.svg';
import { LoadingButton } from '@mui/lab';
import Papa from 'papaparse';
import { Box } from '@mui/system';
import { NestedDropdown } from './mui-nested-menu/components/NestedDropdown';
import AntSwitch from './AntSwitch';
import FileUploader from './FileUploader';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-terminal';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/snippets/json';
import 'ace-builds/src-noconflict/ext-error_marker';
import 'ace-builds/src-min-noconflict/ext-searchbox';
import { Ace } from 'ace-builds';
import AddIcon from '@mui/icons-material/Add';
import { Accordion, AccordionDetails, AccordionSummary } from './Accordian';
import { observer } from 'mobx-react-lite';
import { ConfigStore } from '../stores/ConfigStore';

interface UploadCsvProps {
    progress: Function;
    onSubmit?: Function;
    creating?: boolean;
    disabled?: boolean;
    configStore: ConfigStore;
    expanded?: string | boolean;
    onHandleCreateStepChange?: Function;
}

const UploadCsv = observer((props: UploadCsvProps) => {
    const { t } = useTranslation();
    const { authState, oktaAuth } = useOktaAuth();
    const { masterFiles, lookupFiles, setMasterFiles, setLookupFiles } = props.configStore;
    const { template_config, setTemplateConfig } = props.configStore;
    const { template_json, setTemplateJson } = props.configStore;
    const [upload_in_progress, setUploadInProgress] = useState<boolean>(false);
    const { lookupConfig } = props.configStore;
    const [lookup_config, setLookupConfig] = useState<any>(lookupConfig || []);
    const [code_editor, showCodeEditor] = useState<boolean>(false);
    const [master_files_metadata, setMasterFilesMetadata] = useState<any>([]);
    const [lookup_files_metadata, setLookupFilesMetadata] = useState<any>([]);
    const [from_storage, setFromStorage] = useState<boolean>(false);

    const { configStore } = props;
    const config = configStore.template_config;

    const onHandleLookupConfig = (lc: Array<any>) => {
        setLookupConfig(lc);
        const mappingRawJson = JSON.parse(template_config.environments[0].template.mapping_raw_json);
        mappingRawJson.lookupObjects = lc;
        template_config.environments[0].template.mapping_raw_json = JSON.stringify(mappingRawJson, null, '\t');
        template_config.environments[0].template.mapping = mappingRawJson;
        setTemplateConfig({ ...template_config });
        setTemplateJson(template_config.environments[0].template.mapping_raw_json);
    };

    const onHandleFiles = (masterFiles: Array<File>, lookupFiles: Array<File>) => {
        setMasterFiles(masterFiles);
        setLookupFiles(lookupFiles);
    };

    const handleMasterFilesSelected = (files: any) => {
        const all_files = [...masterFiles];

        for (let f = 0; f < files.length; f++) {
            all_files.push(files[f]);
        }
        setMasterFiles(all_files);

        if (onHandleFiles) {
            onHandleFiles([...all_files], [...lookupFiles]);
        }
    };

    const handleLookupFilesSelected = (files: any) => {
        const all_files = [...lookupFiles];

        for (let f = 0; f < files.length; f++) {
            all_files.push(files[f]);
        }
        setLookupFiles(all_files);

        if (onHandleFiles) {
            onHandleFiles([...masterFiles], [...all_files]);
        }
    };

    const handleMasterFileRemoved = (file: any, index: number) => {
        masterFiles.splice(index, 1);
        setMasterFiles([...masterFiles]);

        if (onHandleFiles) {
            onHandleFiles([...masterFiles], [...lookupFiles]);
        }
    };

    const handleLookupFileRemoved = (file: any, index: number) => {
        lookupFiles.splice(index, 1);
        setLookupFiles([...lookupFiles]);

        if (onHandleFiles) {
            onHandleFiles([...lookupFiles], [...lookupFiles]);
        }
    };

    const handleStepChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
        // read csv meta data only
        if (newExpanded && panel === 'map-files') {
            extractFilesMetadata();
        }
        if (props.creating) {
            props.onHandleCreateStepChange(newExpanded ? panel : false);
        }
    };

    const _extractFilesMetaData = async (files: any, selectCallback: Function) => {
        const fm = [];
        for (let f = 0; f < files.length; f++) {
            // eslint-disable-next-line no-await-in-loop
            const meta: any = await extractFileMetaData(files[f]);
            fm[f] = {
                label: files[f].name,
                items: meta.meta.fields.map((fieldName: any) => ({
                    label: fieldName,
                    callback: ((fileName, fieldName) => (e, index, item, parent) => {
                        console.log(`${fileName}/${fieldName}`);
                        selectCallback(e, index, item, parent, fileName, fieldName);
                    })(files[f].name, fieldName),
                })),
            };
        }
        return fm;
    };

    const extractFilesMetadata = async () => {
        setMasterFilesMetadata(
            await _extractFilesMetaData(masterFiles, (e, index, item, parent, fileName, fieldName) => {
                setLookupConfig((prevLookupConfig) => {
                    const updated_lookup_config = [...prevLookupConfig];
                    updated_lookup_config[index].primaryKeys = [fieldName];
                    updated_lookup_config[index].primaryName = fileName;
                    if (onHandleLookupConfig) {
                        onHandleLookupConfig(updated_lookup_config);
                    }
                    return updated_lookup_config;
                });
            }),
        );

        setLookupFilesMetadata(
            await _extractFilesMetaData(lookupFiles, (e, index, item, parent, fileName, fieldName) => {
                setLookupConfig((prevLookupConfig) => {
                    const updated_lookup_config = [...prevLookupConfig];
                    updated_lookup_config[index].foreignKeys = [fieldName];
                    updated_lookup_config[index].foreignName = fileName;
                    if (onHandleLookupConfig) {
                        onHandleLookupConfig(updated_lookup_config);
                    }
                    return updated_lookup_config;
                });
            }),
        );
    };

    function _setLookupConfig(lu) {
        setLookupConfig(lu);

        if (onHandleLookupConfig) {
            onHandleLookupConfig(lu);
        }
    }

    function extractFileMetaData(file: any) {
        const promise = new Promise((resolve, reject) => {
            Papa.parse(file, {
                worker: true,
                header: true,
                dynamicTyping: true,
                complete(results) {
                    console.log(`done parsing => ${file.name}`);
                    resolve(results);
                },
                error(error) {
                    console.log(`error parsing => ${file.name}`);
                    reject(error);
                },
            });
        });

        return promise;
    }

    async function handleSubmit() {
        // submit files
        setUploadInProgress(true);

        let result;
        if (from_storage) {
            const mfiles = [],
                lfiles = [];

            for (const i in masterFiles) {
                if (masterFiles[i]) {
                    mfiles.push({ file_path: masterFiles[i].name });
                }
            }
            for (const i in lookupFiles) {
                if (lookupFiles[i]) {
                    lfiles.push({ file_path: lookupFiles[i].name });
                }
            }

            result = await Backend.doCsvFromStorage(
                mfiles,
                lfiles,
                config.rowid,
                { with_process: false, with_inject: false, data: {} },
                oktaAuth,
            );
        } else {
            result = await Backend.doCsvUpload(
                masterFiles,
                lookupFiles,
                config.rowid,
                { with_process: false, with_inject: false, data: {} },
                oktaAuth,
            );
        }

        setUploadInProgress(true);
        const row = {
            rowid: result.data.task_id,
            task_config: config.rowid,
        };
        props.onSubmit(row);
    }

    const [lookup_json, setLookupJson] = useState<any>('{}');
    const [codeEditorToggleDisabled, setCodeEditorToggleDisabled] = useState<boolean>(false);

    function handleLookupJsonChange(json: any) {
        setLookupJson(json);
    }

    function handleLookupJsonValidate(annotations: Ace.Annotation[]) {
        setCodeEditorToggleDisabled(annotations.length > 0);
    }

    function handleShowHideCodeEditor(show) {
        if (show) {
            const lj = JSON.stringify(lookup_config, null, '\t');
            setLookupJson(lj);
        } else {
            _setLookupConfig(JSON.parse(lookup_json));
        }

        showCodeEditor(show);
    }

    function addNewLookupConfig() {
        _setLookupConfig([...lookup_config, { primaryKeys: [], foreignKeys: [] }]);
    }

    const removeLookupConfig = (index: number) => {
        _setLookupConfig(lookup_config.filter((item, i) => i !== index));
    };

    function onCsvSourceTypeChanged(fromStorage: boolean) {
        setFromStorage(fromStorage);
        setMasterFiles([]);
        setLookupFiles([]);
    }

    const totalSteps = 3;
    const startStep = props.creating ? 2 : 1;

    return (
        <div className="UploadCsv">
            <Accordion
                disabled={from_storage}
                expanded={props.expanded === 'import-data'}
                onChange={handleStepChange('import-data')}
            >
                <AccordionSummary
                    aria-controls="import-datad-content"
                    expanded={props.expanded === 'import-data'}
                    id="import-datad-header"
                    title={t('import.title')}
                    thisStep={startStep}
                    totalSteps={totalSteps}
                    stepTitle={t('common.accordian-step-title')}
                    data-testid="step2TransferDataTitleContainer"
                    dataTestIdTitle="step2TransferDataTitle"
                    dataTestIdStep="step2TransferDataStep"
                />
                <AccordionDetails data-testid="step2TransferDataContent">
                    <Stack direction="column" spacing={2}>
                        {!props.creating && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography>{t('import.upload-from-computer')}</Typography>
                                <AntSwitch
                                    checked={from_storage}
                                    onChange={() => onCsvSourceTypeChanged(!from_storage)}
                                />
                                <Typography>{t('import.select-from-storage')}</Typography>
                            </Stack>
                        )}
                        <Stack direction="column" className="CsvUpload" spacing={2}>
                            <FileUploader
                                name="masterFiles"
                                title={t('import.upload-master-csv')}
                                tooltipText={t('import.upload-master-tooltip')}
                                onFilesSelected={handleMasterFilesSelected}
                                onFileRemoved={handleMasterFileRemoved}
                                files={masterFiles}
                                isRequired
                                fromStorage={from_storage}
                                {...props}
                            />
                        </Stack>
                        <Stack direction="column" className="CsvUpload" spacing={2}>
                            <FileUploader
                                name="lookupFiles"
                                title={t('import.upload-lookup-csv')}
                                tooltipText={t('import.upload-lookup-tooltip')}
                                onFilesSelected={handleLookupFilesSelected}
                                onFileRemoved={handleLookupFileRemoved}
                                files={lookupFiles}
                                fromStorage={from_storage}
                                {...props}
                            />
                        </Stack>
                        <Button
                            onClick={() => {
                                handleStepChange('map-files')(null, true);
                            }}
                            className={masterFiles.length === 0 ? 'RefreshBtnDisabled' : 'RefreshBtn'}
                            disabled={masterFiles.length === 0}
                            data-testid="data-source-upload-next-btn"
                        >
                            {t('common.next')}{' '}
                            {<ArrowDownIcon className={masterFiles.length === 0 ? 'ArrowIconDisabled' : 'ArrowIcon'} />}
                        </Button>
                    </Stack>
                </AccordionDetails>
            </Accordion>
            <Accordion
                disabled={from_storage}
                expanded={props.expanded === 'map-files'}
                onChange={handleStepChange('map-files')}
            >
                <AccordionSummary
                    aria-controls="map-filesd-content"
                    expanded={props.expanded === 'map-files'}
                    id="map-filesd-header"
                    title={t('import.map-files')}
                    thisStep={startStep + 1}
                    totalSteps={totalSteps}
                    stepTitle={t('common.accordian-step-title')}
                    data-testid="step3MergeDataTitleContainer"
                    dataTestIdTitle="step3MergeDataTitle"
                    dataTestIdStep="step3MergeDataStep"
                />
                <AccordionDetails data-testid="step3MergeDataContent">
                    <Typography>{t('import.map-files-description')}</Typography>

                    <Stack direction="row" justifyContent="flex-end" alignItems="center">
                        <FormControlLabel
                            disabled={codeEditorToggleDisabled}
                            control={
                                <AntSwitch
                                    onChange={() => handleShowHideCodeEditor(!code_editor)}
                                    className="CodeEditorSwitch"
                                />
                            }
                            label={t('import.code-editor') as string}
                        />
                    </Stack>

                    {!code_editor && (
                        <Box sx={{ flexGrow: 1 }}>
                            <Grid container spacing={2} className="MergeSection">
                                <Grid item xs={12} className="Title">
                                    <Grid container>
                                        <Grid item xs={5}>
                                            <div>{t('import.primary-key')}</div>
                                        </Grid>
                                        <Grid item xs={2} className="TitleDividerRoot">
                                            <Divider orientation="vertical" className="Divider" />
                                        </Grid>
                                        <Grid item xs={5}>
                                            <div>{t('import.matching-key')}</div>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {lookup_config
                                    ? lookup_config.map((lo: any, index: number) => (
                                          <Grid key={`lookup-config-${index}`} item xs={12} className="Row">
                                              <Grid container>
                                                  <Grid item xs={6}>
                                                      <Grid container>
                                                          <Grid item xs={11}>
                                                              <NestedDropdown
                                                                  index={index}
                                                                  menuItemsData={{
                                                                      items: master_files_metadata,
                                                                      label: lo.primaryName
                                                                          ? `${lo.primaryName} / ${lo.primaryKeys[0]}`
                                                                          : 'Select',
                                                                  }}
                                                              />
                                                          </Grid>
                                                          <Grid className="Icon" item xs={1}>
                                                              <img
                                                                  alt=""
                                                                  src={LookupConfigMergeIcon}
                                                                  className="Image"
                                                              />
                                                          </Grid>
                                                      </Grid>
                                                  </Grid>

                                                  <Grid item xs={6}>
                                                      <Grid container>
                                                          <Grid item xs={11}>
                                                              <NestedDropdown
                                                                  index={index}
                                                                  menuItemsData={{
                                                                      items: lookup_files_metadata,
                                                                      label: lo.foreignName
                                                                          ? `${lo.foreignName} / ${lo.foreignKeys[0]}`
                                                                          : 'Select',
                                                                  }}
                                                              />
                                                          </Grid>
                                                          <Grid className="Icon" item xs={1}>
                                                              <Button onClick={() => removeLookupConfig(index)}>
                                                                  <img
                                                                      alt=""
                                                                      src={LookupConfigDeleteIcon}
                                                                      className="Image"
                                                                  />
                                                              </Button>
                                                          </Grid>
                                                      </Grid>
                                                  </Grid>
                                              </Grid>
                                          </Grid>
                                      ))
                                    : null}
                                <Grid item xs={2}>
                                    <Button startIcon={<AddIcon />} onClick={() => addNewLookupConfig()}>
                                        {t('import.add-new-config')}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                    {code_editor ? (
                        <Box sx={{ flexGrow: 1 }}>
                            <AceEditor
                                className="CodeEditor"
                                mode="json"
                                theme="terminal"
                                onChange={(json: any) => {
                                    handleLookupJsonChange(json);
                                }}
                                fontSize={18}
                                showPrintMargin
                                showGutter
                                highlightActiveLine
                                value={lookup_json}
                                setOptions={{
                                    enableBasicAutocompletion: true,
                                    enableLiveAutocompletion: true,
                                    enableSnippets: true,
                                    showLineNumbers: true,
                                    tabSize: 4,
                                    useWorker: true,
                                }}
                                onValidate={(annotations: Ace.Annotation[]) => {
                                    handleLookupJsonValidate(annotations);
                                }}
                            />
                        </Box>
                    ) : null}
                </AccordionDetails>
            </Accordion>
            {!props.creating && (
                <Accordion expanded={props.expanded === 'review-submit'} onChange={handleStepChange('review-submit')}>
                    <AccordionSummary
                        aria-controls="review-submitd-content"
                        expanded={props.expanded === 'review-submit'}
                        id="review-submitd-header"
                        title={t('import.review-submit')}
                        thisStep={3}
                        totalSteps={totalSteps}
                        stepTitle={t('common.accordian-step-title')}
                    />
                    <AccordionDetails>
                        <Stack direction="column" spacing={2} alignItems="flex-start">
                            <Typography>{`${t('import.review-submit-master-files')}: ${masterFiles.length}`}</Typography>
                            <Typography>{`${t('import.review-submit-lookup-files')}: ${lookupFiles.length}`}</Typography>
                            <LoadingButton
                                loading={upload_in_progress}
                                variant="contained"
                                onClick={() => handleSubmit()}
                                disabled={masterFiles.length === 0}
                            >
                                {t('import.submit')}
                            </LoadingButton>
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}
        </div>
    );
});

export default UploadCsv;
